/**
 * Shared · PlacesAutocomplete — pillar-agnostic typeahead overlay.
 *
 * Drop-in replacement for any pillar search input. Wraps Google
 * Places Autocomplete via `/api/search/autocomplete` with session-
 * token billing, debounce, and a clean dropdown UI on-brand with the
 * Concierge® palette.
 *
 * Usage:
 *   <PlacesAutocomplete
 *     value={q}
 *     onChange={setQ}
 *     onSubmit={(picked) => runSearch(picked)}
 *     placeholder="Where would you like to stay?"
 *     types="lodging"                         // optional · default 'establishment'
 *     testidPrefix="stay-autocomplete"        // optional
 *     style={{ ... }}                         // optional input override
 *   />
 *
 * `types` values:
 *   • "establishment"  → venues (restaurants, gyms, attractions) · default
 *   • "lodging"        → hotels (for /stay)
 *   • "(cities)"       → destinations (for /escape, /mobility)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function newSessionToken() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return "lc-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}


export function PlacesAutocomplete({
    value,
    onChange,
    onSubmit,
    placeholder = "Search…",
    types = "establishment",
    testidPrefix = "autocomplete",
    inputStyle = {},
    containerStyle = {},
    autoFocus = false,
}) {
    const [focus, setFocus] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [suggestions, setSuggestions] = useState([]);
    const [sessionToken, setSessionToken] = useState(() => newSessionToken());
    const inputRef = useRef(null);

    // Debounced server call · 250ms after last keystroke.
    useEffect(() => {
        if (!focus) { setSuggestions([]); return; }
        const text = (value || "").trim();
        if (text.length < 2) { setSuggestions([]); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const r = await fetch(
                    `${API}/api/search/autocomplete?q=${encodeURIComponent(text)}&session_token=${encodeURIComponent(sessionToken)}&types=${encodeURIComponent(types)}`,
                );
                if (!r.ok) throw new Error("autocomplete_failed");
                const j = await r.json();
                if (!cancelled) setSuggestions(j.suggestions || []);
            } catch {
                if (!cancelled) setSuggestions([]);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [value, focus, types, sessionToken]);

    const handlePick = (s) => {
        onChange?.(s.fill);
        setSuggestions([]);
        setHighlight(-1);
        // Mint a fresh session token after each pick · Google's
        // session billing rules.
        setSessionToken(newSessionToken());
        onSubmit?.(s.fill, s);
    };

    const handleKeyDown = (e) => {
        if (!suggestions.length) {
            if (e.key === "Enter" && value) {
                e.preventDefault();
                onSubmit?.(value, null);
            }
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => (h + 1) % suggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlight >= 0) {
                handlePick(suggestions[highlight]);
            } else if (value) {
                onSubmit?.(value, null);
            }
        } else if (e.key === "Escape") {
            setHighlight(-1);
            setSuggestions([]);
        }
    };

    const showOverlay = focus && suggestions.length > 0;

    const baseInputStyle = useMemo(() => ({
        width: "100%",
        padding: "12px 16px",
        background: "var(--t-card, #fff)",
        border: "1.5px solid var(--t-line2, rgba(43,35,26,0.1))",
        borderRadius: 14,
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        fontSize: 15,
        color: "var(--t-ink, #2b231a)",
        outline: "none",
        transition: "border-color 150ms ease",
        ...inputStyle,
    }), [inputStyle]);

    return (
        <div
            data-testid={`${testidPrefix}-container`}
            style={{ position: "relative", width: "100%", ...containerStyle }}
        >
            <input
                ref={inputRef}
                data-testid={`${testidPrefix}-input`}
                type="search"
                value={value || ""}
                onChange={(e) => { onChange?.(e.target.value); setHighlight(-1); }}
                onFocus={() => setFocus(true)}
                onBlur={() => setTimeout(() => setFocus(false), 180)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                autoComplete="off"
                autoFocus={autoFocus}
                style={baseInputStyle}
            />
            {showOverlay && (
                <div
                    data-testid={`${testidPrefix}-dropdown`}
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        marginTop: 8,
                        background: "var(--t-card, #fff)",
                        border: "1px solid var(--t-line2, rgba(43,35,26,0.1))",
                        borderRadius: 14,
                        boxShadow: "0 18px 48px rgba(43,35,26,0.14), 0 4px 12px rgba(43,35,26,0.06)",
                        overflow: "hidden",
                        zIndex: 60,
                    }}
                >
                    {suggestions.map((s, i) => {
                        const active = i === highlight;
                        return (
                            <button
                                key={`${s.kind}-${s.label}-${i}`}
                                type="button"
                                data-testid={`${testidPrefix}-item-${i}`}
                                data-active={active ? "true" : "false"}
                                onMouseDown={(e) => { e.preventDefault(); handlePick(s); }}
                                onMouseEnter={() => setHighlight(i)}
                                style={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    width: "100%",
                                    padding: "10px 14px",
                                    border: 0,
                                    background: active ? "var(--t-paper2, rgba(244,239,230,0.6))" : "transparent",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    transition: "background 80ms ease",
                                    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                                }}
                            >
                                <span style={{ fontSize: 14, color: "var(--t-ink, #2b231a)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {s.label}
                                </span>
                                <span
                                    className="t-mono uppercase"
                                    style={{
                                        fontSize: 9,
                                        letterSpacing: "0.18em",
                                        color: "var(--t-ink3, rgba(43,35,26,0.55))",
                                        whiteSpace: "nowrap",
                                        flexShrink: 0,
                                    }}
                                >
                                    {s.hint || s.kind}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
